import { clientId, redirectUri, scopes } from "./spotify/spotify-auth";
import * as appleAuth from "./apple/apple-auth";
import { apiGet, apiPost } from "./api";
import { MusicProvider } from "./musicProvider";

export interface ProviderStatus {
    connected: boolean;
    /** Empty when connected but the provider gave us nothing to show (or not connected). */
    username: string;
}

/**
 * Full-page redirect to Spotify's OAuth screen. SpotifyRedirect.tsx handles the code
 * exchange on the way back. Shared between Settings and the in-dashboard "Connect"
 * prompt so both trigger the exact same flow.
 */
export function connectSpotify(fromSettings: boolean): void {
    document.cookie = `lastPage=${fromSettings ? "settings" : "dashboard"}; path=/; max-age=${60 * 60}`;
    const authUrl = `https://accounts.spotify.com/authorize?client_id=${clientId}&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(scopes)}`;
    window.location.href = authUrl;
}

/** Opens Apple's own sign-in UI via MusicKit; the user completes it themselves. */
export async function connectApple(): Promise<void> {
    await appleAuth.connect();
}

export async function fetchProviderStatus(provider: MusicProvider): Promise<ProviderStatus> {
    if (provider === "Spotify") {
        return apiGet<ProviderStatus>('/v1/spotify/status');
    }
    const status = await apiGet<{ configured: boolean; connected: boolean }>('/v1/apple/status');
    // Apple/MusicKit exposes no API for the account's display name - there is
    // deliberately no username to show here, unlike Spotify.
    return { connected: status.connected, username: "" };
}

export async function disconnectProvider(provider: MusicProvider): Promise<void> {
    if (provider === "Spotify") {
        await apiPost('/v1/spotify/disconnect', {});
        // apiPost throws on non-2xx before the caller sees anything, so this only
        // runs once the backend row is actually gone. LinkButton's Spotify picker
        // reads this cookie directly (not our backend), so it has to go too.
        document.cookie = "spotifyAuthToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
        return;
    }
    await apiPost('/v1/apple/disconnect', {});
    try {
        // Best-effort: revoke MusicKit's own client-side session too. If MusicKit
        // was never configured in this tab there's nothing to revoke - either way
        // the backend row (the part that actually gates sync) is already gone.
        await appleAuth.disconnect();
    } catch {
        // ignore
    }
}
